import { useState, useCallback } from "react";
import PageShell from "../../../components/PageShell";
import DataTable from "../../../components/DataTable";
import DataForm from "../../../components/DataForm";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../../../components/ui/dialog";
import { Button } from "../../../components/ui/button";
import {
  useMediaContents,
  useSaveMediaContent,
  useMediaFileNames,
} from "../hooks/useMediaContents";
import { useLocationOptions } from "../hooks/useOptions";
import MediaContentManager from "../../../components/MediaContentManager";
import toast from "react-hot-toast";
import { useTranslation } from "../../../i18n";

const COLUMNS = [
  { header: "col.id", accessor: "id", type: "number" },
  { header: "col.locationId", accessor: "locationId", type: "number" },
  { header: "col.dayType", accessor: "dayType", type: "number" },
  { header: "col.filePath", accessor: "filePath", type: "text" },
];

const CREATE_DEFAULTS = { locationId: "", dayType: 1, filePath: "" };

export default function MediaContentPage() {
  const { t } = useTranslation();
  const { data: mediaContents, isLoading } = useMediaContents();
  const { data: fileNames } = useMediaFileNames();
  const { data: locationOptions } = useLocationOptions();
  const saveMutation = useSaveMediaContent();
  const [editingRow, setEditingRow] = useState(null);
  const [isNew, setIsNew] = useState(false);
  const [values, setValues] = useState(null);
  const [showFileManager, setShowFileManager] = useState(false);

  const openEdit = useCallback((row) => {
    setEditingRow(row);
    setValues({ ...row });
    setIsNew(false);
  }, []);

  const openCreate = useCallback(() => {
    setEditingRow(CREATE_DEFAULTS);
    setValues({ ...CREATE_DEFAULTS });
    setIsNew(true);
  }, []);

  const handleSave = () => {
    if (!values) return;
    saveMutation.mutate(values, {
      onSuccess: () => {
        toast.success(
          isNew ? t("toast.recordCreated") : t("toast.recordUpdated"),
        );
        setEditingRow(null);
        setValues(null);
      },
      onError: () => toast.error(t("toast.recordSaveFailed")),
    });
  };

  const filePathOptions = fileNames?.map((f) => ({ value: f, name: f })) || [];

  const formOptions = {
    locationId: locationOptions,
    filePath: filePathOptions,
  };

  const FORM_FIELDS = [
    { name: "id", label: "col.id", type: "number", disabled: true },
    { name: "locationId", label: "form.locationId", type: "select" },
    { name: "dayType", label: "form.dayType", type: "number" },
    { name: "filePath", label: "form.filePath", type: "select" },
  ];

  const actions = (
    <>
      <Button onClick={openCreate}>{t("common.add")}</Button>
      <Button
        variant="secondary"
        onClick={() => setShowFileManager(!showFileManager)}
      >
        {showFileManager
          ? t("fileManager.showTable")
          : t("fileManager.manageFiles")}
      </Button>
    </>
  );

  return (
    <PageShell title={t("page.media")} actions={actions}>
      {isLoading ? (
        <p className="text-c-gray-400">{t("common.loading")}</p>
      ) : showFileManager ? (
        <MediaContentManager
          files={fileNames || []}
          onFilesChange={() => {
            /* refreshed via query */
          }}
        />
      ) : (
        <DataTable
          columns={COLUMNS}
          data={mediaContents}
          onRowClick={openEdit}
        />
      )}
      <Dialog
        open={!!editingRow}
        onOpenChange={(o) => {
          if (!o) {
            setEditingRow(null);
            setValues(null);
          }
        }}
      >
        <DialogContent className="left-auto right-0 top-0 translate-x-0 translate-y-0 h-dvh max-h-none rounded-none sm:rounded-none border-r-0 border-t-0 border-b-0 data-[state=closed]:slide-out-to-right data-[state=open]:slide-in-from-right min-w-[480px] grid-rows-[auto_1fr_auto]">
          <DialogHeader>
            <DialogTitle>
              {isNew ? t("dialog.addMedia") : t("dialog.editRecord")}
            </DialogTitle>
          </DialogHeader>
          <form
            id="edit-form"
            onSubmit={(e) => {
              e.preventDefault();
              handleSave();
            }}
            className="flex-1 overflow-y-auto px-2"
          >
            <DataForm
              fields={FORM_FIELDS}
              values={values}
              onChange={setValues}
              options={formOptions}
            />
          </form>
          <DialogFooter className="flex gap-2">
            <Button type="submit" form="edit-form">
              {t("common.save")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </PageShell>
  );
}
